import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { serviceRequestService } from '../../services/serviceRequestService';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const CustomerDashboard = () => {
  const { user, userProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    setLoading(true);
    setError('');

    try {
      const { data, error: fetchError } = await serviceRequestService?.getUserRequests();
      
      if (fetchError) throw fetchError;
      
      setRequests(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/signin');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-warning text-warning-foreground',
      contacted: 'bg-blue-500 text-white',
      scheduled: 'bg-purple-500 text-white',
      completed: 'bg-success text-success-foreground',
      cancelled: 'bg-error text-error-foreground'
    };
    return colors?.[status] || 'bg-gray-500 text-white';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: 'Clock',
      contacted: 'Phone',
      scheduled: 'Calendar',
      completed: 'CheckCircle',
      cancelled: 'XCircle'
    };
    return icons?.[status] || 'Circle';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="font-body text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon name="Wrench" size={32} color="var(--color-primary)" />
              <div>
                <h1 className="font-headline font-bold text-xl text-foreground">My Dashboard</h1>
                <p className="font-body text-sm text-muted-foreground">meriodine</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden md:block">
                <p className="font-body text-sm font-medium text-foreground">{userProfile?.full_name}</p>
                <p className="font-body text-xs text-muted-foreground">{userProfile?.email}</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleSignOut}>
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>
      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-primary to-accent rounded-lg p-6 md:p-8 mb-8 text-white">
          <h2 className="font-headline font-bold text-2xl md:text-3xl mb-2">
            Welcome back, {userProfile?.full_name?.split(' ')?.[0]}!
          </h2>
          <p className="font-body text-white/90 mb-4">
            Track your service requests and manage your account
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="secondary"
              onClick={() => navigate('/contact')}
            >
              <Icon name="Plus" size={16} className="mr-2" />
              New Service Request
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.href = 'tel:0827935785'}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              <Icon name="Phone" size={16} className="mr-2" />
              Emergency: 082 793 5785
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-error/10 border border-error text-error rounded-lg p-4 mb-6">
            {error}
          </div>
        )}

        {/* Service Requests */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-headline font-bold text-2xl text-foreground">My Service Requests</h3>
            <Button variant="outline" size="sm" onClick={loadRequests}>
              <Icon name="RefreshCw" size={16} className="mr-2" />
              Refresh
            </Button>
          </div>

          {requests?.length === 0 ? (
            <div className="bg-card rounded-lg border border-border p-12 text-center">
              <Icon name="Inbox" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <h4 className="font-headline font-bold text-lg text-foreground mb-2">No Service Requests Yet</h4>
              <p className="font-body text-muted-foreground mb-4">
                You haven't submitted any service requests. Need help with plumbing or electrical work?
              </p>
              <Button onClick={() => navigate('/contact')}>
                Request Service
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {requests?.map((request) => (
                <div key={request?.id} className="bg-card rounded-lg border border-border p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <Icon name={getStatusIcon(request?.status)} size={20} className="text-primary" />
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request?.status)}`}>
                          {request?.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm font-body">
                        <div>
                          <p className="text-muted-foreground mb-1">Service Type</p>
                          <p className="font-medium text-foreground">{request?.service_type}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground mb-1">Urgency</p>
                          <p className="font-medium text-foreground capitalize">{request?.urgency}</p>
                        </div>
                        {request?.preferred_time && (
                          <div>
                            <p className="text-muted-foreground mb-1">Preferred Time</p>
                            <p className="font-medium text-foreground capitalize">{request?.preferred_time}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-muted-foreground mb-1">Submitted</p>
                          <p className="font-medium text-foreground">
                            {new Date(request.created_at)?.toLocaleDateString('en-ZA', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                      {request?.message && (
                        <div className="mt-4 p-3 bg-accent/10 rounded">
                          <p className="text-sm font-body text-muted-foreground mb-1">Your Message:</p>
                          <p className="text-sm font-body text-foreground">{request?.message}</p>
                        </div>
                      )}
                      {request?.admin_notes && (
                        <div className="mt-3 p-3 bg-primary/10 rounded border border-primary/20">
                          <p className="text-sm font-body text-primary mb-1 font-medium">Admin Notes:</p>
                          <p className="text-sm font-body text-foreground">{request?.admin_notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="font-headline font-bold text-xl text-foreground mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate('/plumbing-services')}
              className="p-4 border border-border rounded-lg hover:bg-accent/10 transition-colors text-left"
            >
              <Icon name="Droplets" size={24} className="text-primary mb-2" />
              <p className="font-body font-medium text-foreground">Plumbing Services</p>
              <p className="font-body text-sm text-muted-foreground">View our plumbing solutions</p>
            </button>
            <button
              onClick={() => navigate('/electrical-services')}
              className="p-4 border border-border rounded-lg hover:bg-accent/10 transition-colors text-left"
            >
              <Icon name="Zap" size={24} className="text-primary mb-2" />
              <p className="font-body font-medium text-foreground">Electrical Services</p>
              <p className="font-body text-sm text-muted-foreground">Explore electrical solutions</p>
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="p-4 border border-border rounded-lg hover:bg-accent/10 transition-colors text-left"
            >
              <Icon name="MessageSquare" size={24} className="text-primary mb-2" />
              <p className="font-body font-medium text-foreground">Contact Us</p>
              <p className="font-body text-sm text-muted-foreground">Get in touch with our team</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;